import React, { useState, useMemo } from 'react';

const ConflictResolutionUI = ({ conflicts, onSubmitResolutions, isSubmitting }) => {
  const [resolutions, setResolutions] = useState({});
  const [filterType, setFilterType] = useState('all');
  const [bulkAction, setBulkAction] = useState('');

  // Initialize resolution state for each conflict
  React.useEffect(() => {
    const initialResolutions = {};
    conflicts.forEach(conflict => {
      initialResolutions[conflict.row] = {
        action: '', // user hasn't chosen yet
        update_fields: ['overall_deadline', 'urgency_level', 'tasks']
      };
    });
    setResolutions(initialResolutions);
  }, [conflicts]);

  // Filter conflicts by type
  const filteredConflicts = useMemo(() => {
    if (filterType === 'all') return conflicts;
    return conflicts.filter(c => c.type === filterType);
  }, [conflicts, filterType]);

  // Get unique conflict types
  const conflictTypes = useMemo(() => {
    const types = new Set(conflicts.map(c => c.type));
    return Array.from(types);
  }, [conflicts]);

  // Handle individual action change
  const handleActionChange = (row, action) => {
    setResolutions(prev => ({
      ...prev,
      [row]: {
        ...prev[row],
        action
      }
    }));
  };

  // Handle update fields change
  const handleFieldToggle = (row, field) => {
    setResolutions(prev => {
      const currentFields = prev[row]?.update_fields || [];
      const newFields = currentFields.includes(field)
        ? currentFields.filter(f => f !== field)
        : [...currentFields, field];

      return {
        ...prev,
        [row]: {
          ...prev[row],
          update_fields: newFields
        }
      };
    });
  };

  // Handle bulk action
  const handleBulkAction = () => {
    if (!bulkAction) return;

    const newResolutions = { ...resolutions };
    filteredConflicts.forEach(conflict => {
      newResolutions[conflict.row] = {
        ...newResolutions[conflict.row],
        action: bulkAction
      };
    });

    setResolutions(newResolutions);
    setBulkAction('');
  };

  // Submit resolutions
  const handleSubmit = () => {
    // Build resolutions array
    const resolutionsList = Object.entries(resolutions)
      .filter(([row, resolution]) => resolution.action) // Only include rows with action selected
      .map(([row, resolution]) => ({
        row: parseInt(row),
        action: resolution.action,
        update_fields: resolution.action === 'update' ? resolution.update_fields : undefined
      }));

    if (resolutionsList.length === 0) {
      alert('Please select actions for at least one conflict');
      return;
    }

    onSubmitResolutions(resolutionsList);
  };

  // Get action label
  const getActionLabel = (action) => {
    const labels = {
      'update': 'Update Existing',
      'create_new': 'Create New',
      'skip': 'Skip',
      'reopen': 'Reopen & Update'
    };
    return labels[action] || action;
  };

  // Get conflict type badge
  const getConflictTypeBadge = (type) => {
    const badges = {
      'exact_duplicate': { bg: 'bg-warning', text: 'Exact Duplicate' },
      'potential_update': { bg: 'bg-info', text: 'Potential Update' },
      'completed_referral': { bg: 'bg-secondary', text: 'Completed Referral' },
      'csv_duplicate': { bg: 'bg-danger', text: 'CSV Duplicate' }
    };

    const badge = badges[type] || { bg: 'bg-secondary', text: type };
    return <span className={`badge ${badge.bg}`}>{badge.text}</span>;
  };

  // Count resolved conflicts
  const resolvedCount = Object.values(resolutions).filter(r => r.action).length;
  const totalConflicts = filteredConflicts.length;

  return (
    <div className="conflict-resolution-ui">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-exclamation-triangle text-warning me-2"></i>
            Resolve Conflicts ({resolvedCount}/{totalConflicts} resolved)
          </h5>
        </div>

        <div className="card-body">
          {/* Filter and Bulk Actions Toolbar */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label small">Filter by Type:</label>
              <select
                className="form-select form-select-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Conflicts ({conflicts.length})</option>
                {conflictTypes.map(type => {
                  const count = conflicts.filter(c => c.type === type).length;
                  return (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')} ({count})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label small">Bulk Action:</label>
              <div className="input-group input-group-sm">
                <select
                  className="form-select"
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                >
                  <option value="">-- Select Action --</option>
                  <option value="update">Update All</option>
                  <option value="create_new">Create New for All</option>
                  <option value="skip">Skip All</option>
                  <option value="reopen">Reopen All</option>
                </select>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                >
                  Apply to Filtered ({filteredConflicts.length})
                </button>
              </div>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-success btn-sm w-100"
                onClick={handleSubmit}
                disabled={resolvedCount === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-2"></i>
                    Submit All
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Conflicts Table */}
          <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table className="table table-sm table-hover">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: '60px' }}>Row</th>
                  <th style={{ width: '150px' }}>Type</th>
                  <th>Patient</th>
                  <th>Procedure</th>
                  <th>Existing Referral</th>
                  <th style={{ width: '200px' }}>Action</th>
                  <th style={{ width: '250px' }}>Fields to Update</th>
                </tr>
              </thead>
              <tbody>
                {filteredConflicts.map((conflict, idx) => {
                  const resolution = resolutions[conflict.row] || {};
                  const showFieldSelection = resolution.action === 'update' || resolution.action === 'reopen';

                  return (
                    <tr key={idx} className={resolution.action ? 'table-success' : ''}>
                      <td className="text-center">{conflict.row}</td>
                      <td>{getConflictTypeBadge(conflict.type)}</td>
                      <td>
                        <div className="small">
                          <strong>
                            {conflict.patient_info.first_name} {conflict.patient_info.last_name}
                          </strong>
                          <br />
                          <span className="text-muted">DOB: {conflict.patient_info.dob}</span>
                          {conflict.patient_info.mrn && (
                            <>
                              <br />
                              <span className="text-muted">MRN: {conflict.patient_info.mrn}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-primary">{conflict.case_name}</span>
                      </td>
                      <td>
                        {conflict.existing_referral ? (
                          <div className="small">
                            <a
                              href={`/admin/referrals/${conflict.existing_referral.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              View Referral
                            </a>
                            <br />
                            <span className="text-muted">
                              {conflict.existing_referral.case_name}
                            </span>
                            {conflict.existing_referral.is_complete && (
                              <>
                                <br />
                                <span className="badge bg-secondary">Completed</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">
                            {conflict.type === 'csv_duplicate' ? (
                              <>Row {conflict.duplicate_csv_row}</>
                            ) : (
                              '-'
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={resolution.action || ''}
                          onChange={(e) => handleActionChange(conflict.row, e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {conflict.suggested_actions.map(action => (
                            <option key={action} value={action}>
                              {getActionLabel(action)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {showFieldSelection ? (
                          <div className="small">
                            {['overall_deadline', 'urgency_level', 'tasks', 'description', 'assigned_to'].map(field => (
                              <div key={field} className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`field-${conflict.row}-${field}`}
                                  checked={resolution.update_fields?.includes(field) || false}
                                  onChange={() => handleFieldToggle(conflict.row, field)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`field-${conflict.row}-${field}`}
                                >
                                  {field.replace(/_/g, ' ')}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredConflicts.length === 0 && (
            <div className="text-center text-muted py-4">
              No conflicts found for selected filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionUI;
